import type { SystemStyleObject, Theme } from '@mui/system';
import { useFieldWarningEvaluation } from '@src/shared/form/FieldWarningEvaluationContext';
import useFormMethodsContext from '@src/shared/form/useFormMethodsContext';
import Avatar from '@src/shared/ui/Avatar';
import { Button } from '@src/shared/ui/Button';
import { FormHelperText } from '@src/shared/ui/form';
import { AccountBoxIcon, DeleteIcon, UploadIcon } from '@src/shared/ui/icons';
import { Box } from '@src/shared/ui/layout';
import { type ChangeEvent, useId } from 'react';
import {
  type Control,
  type FieldValues,
  useController,
  useWatch,
} from 'react-hook-form';

const StyleSheet = {
  root: {
    mb: 2.5,
    display: 'flex',
    alignItems: 'center',
  },
  avatar: {
    backgroundColor: 'grey.200',
    borderRadius: 1,
    mr: 1.25,
    width: 40,
    height: 40,
  },
};

type FormContextShape = {
  control: Control<FieldValues>;
  setValue: (
    name: string,
    value: unknown,
    options?: { shouldDirty?: boolean; shouldValidate?: boolean },
  ) => void;
};

type ImageUploadFieldProps = {
  name: string;
  label: string;
  disabled?: boolean;
  error?: boolean;
  onValueChange?: () => void;
  styles?: {
    image?: SystemStyleObject<Theme>;
    root?: SystemStyleObject<Theme>;
  };
  EmptyFiller?: React.ReactNode;
};

function ImageUploadField({
  name,
  label,
  disabled = false,
  error = false,
  onValueChange,
  styles = {},
  EmptyFiller,
}: ImageUploadFieldProps) {
  const { control, setValue } = useFormMethodsContext() as FormContextShape;
  const warningEvaluation = useFieldWarningEvaluation();
  const { field, fieldState } = useController({ control, name });
  const helperTextId = useId();
  const previewName = `${name}Preview`;
  const imagePreview = useWatch({ control, name: previewName }) as
    | string
    | null;
  const hasError = Boolean(fieldState.error) || error;
  const errorButtonStyles = hasError
    ? {
        borderColor: 'error.main',
        color: 'error.main',
        '&:hover': {
          borderColor: 'error.main',
        },
      }
    : undefined;

  const clearImage = () => {
    setValue(name, null, { shouldDirty: true, shouldValidate: true });
    setValue(previewName, null, { shouldDirty: true, shouldValidate: true });
    onValueChange?.();
    warningEvaluation?.changeValue(name);
    warningEvaluation?.commitValue(name);
  };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    setValue(name, file, { shouldDirty: true, shouldValidate: true });
    warningEvaluation?.changeValue(name);
    warningEvaluation?.commitValue(name);

    if (!file) {
      setValue(previewName, null, { shouldDirty: true, shouldValidate: true });
      return;
    }

    onValueChange?.();

    const reader = new FileReader();
    reader.onload = () => {
      setValue(
        previewName,
        typeof reader.result === 'string' ? reader.result : null,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box
      sx={{
        ...(styles.root || StyleSheet.root),
        flexWrap: 'wrap',
      }}
    >
      <Avatar
        src={imagePreview ?? undefined}
        sx={styles.image || StyleSheet.avatar}
      >
        {EmptyFiller ?? <AccountBoxIcon />}
      </Avatar>

      {imagePreview ? (
        <Button
          ref={field.ref}
          variant="outlined"
          onClick={clearImage}
          onBlur={field.onBlur}
          disabled={disabled}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? helperTextId : undefined}
          startIcon={<DeleteIcon color="inherit" />}
          sx={errorButtonStyles}
        >
          Delete {label}
        </Button>
      ) : (
        <Button
          ref={field.ref}
          component="label"
          variant="outlined"
          disabled={disabled}
          onBlur={field.onBlur}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? helperTextId : undefined}
          startIcon={<UploadIcon color="inherit" />}
          sx={errorButtonStyles}
        >
          Upload {label}
          <input
            hidden
            name={field.name}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
        </Button>
      )}
      {fieldState.error?.message && (
        <FormHelperText
          id={helperTextId}
          error
          sx={{ flexBasis: '100%', ml: 0 }}
        >
          {fieldState.error.message}
        </FormHelperText>
      )}
    </Box>
  );
}

export default ImageUploadField;
